import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Landmark, TrendingUp, TrendingDown, DollarSign, Wallet, AlertTriangle,
  Loader2, BarChart3, ArrowUpRight, ArrowDownRight, Info, ShieldAlert,
  RefreshCw, ChevronRight, PiggyBank, Receipt, FileText, Download, Settings2, Target
} from "lucide-react";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, ComposedChart, Line, ReferenceLine
} from "recharts";
import { KpiSkeleton, TableSkeleton } from "../components/Skeletons";
import BankTab from "../components/tresorerie/BankTab";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const fmt = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
const fmtK = (v) => {
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(0)}k`;
  return v?.toString() || "0";
};

const ALERT_STYLES = {
  danger: { bg: "#dc262615", border: "#dc262640", text: "#ef4444", icon: ShieldAlert },
  warning: { bg: "#f59e0b15", border: "#f59e0b40", text: "#f59e0b", icon: AlertTriangle },
  info: { bg: "#3b82f615", border: "#3b82f640", text: "#60a5fa", icon: Info },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border px-4 py-3 shadow-xl" style={{ background: "#14101c", borderColor: "rgba(255,255,255,0.1)" }}>
      <p className="text-xs font-bold mb-2" style={{ color: "#f2f3f7" }}>
        {label ? new Date(label + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : ""}
      </p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-4 text-xs py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="font-bold" style={{ color: p.color }}>{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function TresoreriePage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();

  const [dashboard, setDashboard] = useState(null);
  const [cashflow, setCashflow] = useState([]);
  const [projData, setProjData] = useState(null);
  const [comparatif, setComparatif] = useState(null);
  const [objectifs, setObjectifs] = useState(null);
  const [showObjConfig, setShowObjConfig] = useState(false);
  const [objForm, setObjForm] = useState([]);
  const [savingObj, setSavingObj] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [bankSummary, setBankSummary] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashR, cfR, projR, compR, objR, bankR] = await Promise.all([
        api.get("/tresorerie/dashboard"),
        api.get("/tresorerie/cashflow?months_back=11"),
        api.get("/tresorerie/projection?months_forward=6"),
        api.get("/tresorerie/comparatif"),
        api.get("/tresorerie/objectifs"),
        api.get("/tresorerie/bank/summary").catch(() => ({ data: null })),
      ]);
      setDashboard(dashR.data);
      setCashflow(cfR.data?.cashflow || []);
      setProjData(projR.data);
      setComparatif(compR.data);
      setObjectifs(objR.data);
      setBankSummary(bankR.data);
    } catch (e) {
      console.error("Tresorerie load error:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const kpis = dashboard?.kpis || {};
  const alerts = dashboard?.alerts || {};

  const openObjConfig = () => {
    const available = objectifs?.available_types || [];
    const current = objectifs?.objectifs || [];
    const form = available.map(t => {
      const existing = current.find(o => o.type === t.type);
      return { type: t.type, label: t.label, unit: t.unit, target: existing?.target || "" };
    });
    setObjForm(form);
    setShowObjConfig(true);
  };

  const saveObjectifs = async () => {
    setSavingObj(true);
    try {
      const month = dashboard?.month || new Date().toISOString().slice(0, 7);
      const payload = objForm.filter(o => o.target > 0).map(o => ({ type: o.type, target: parseFloat(o.target) }));
      await api.put("/tresorerie/objectifs", { month, objectifs: payload });
      const objR = await api.get(`/tresorerie/objectifs?month=${month}`);
      setObjectifs(objR.data);
      setShowObjConfig(false);
    } catch (e) {
      console.error("Save objectifs error:", e);
    }
    setSavingObj(false);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const monthStr = dashboard?.month ? new Date(dashboard.month + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "";

    // Title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Rapport de Tresorerie", 14, 18);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100);
    doc.text(`Genere le ${new Date().toLocaleDateString("fr-FR")} — Periode: ${monthStr}`, 14, 25);

    // KPIs section
    let y = 35;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0);
    doc.text("Indicateurs cles", 14, y);
    y += 8;

    const kpiData = [
      ["Solde net", fmt(kpis.solde_net)],
      ["Total encaissements", fmt(kpis.total_encaissements)],
      ["Total regle", fmt(kpis.total_regle)],
      ["Total depenses", fmt(kpis.total_depenses)],
      ["Reste a regler", fmt(kpis.total_reste_a_regler)],
      ["Taux recouvrement", `${kpis.taux_recouvrement || 0}%`],
    ];
    autoTable(doc, {
      startY: y, head: [["Indicateur", "Valeur"]], body: kpiData,
      theme: "grid", headStyles: { fillColor: [247, 201, 53], textColor: [11, 8, 17], fontStyle: "bold" },
      styles: { fontSize: 9 }, margin: { left: 14 },
    });

    // Alerts
    if (alerts.length > 0) {
      y = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Alertes", 14, y);
      y += 6;
      autoTable(doc, {
        startY: y, head: [["Type", "Message"]], body: alerts.map(a => [a.type, a.message]),
        theme: "grid", headStyles: { fillColor: [239, 68, 68], textColor: 255, fontStyle: "bold" },
        styles: { fontSize: 8 }, margin: { left: 14 },
      });
    }

    // Cash-flow table
    const cfData = cashflow.filter(c => c.entrees > 0 || c.sorties > 0);
    if (cfData.length > 0) {
      y = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Cash-Flow mensuel", 14, y);
      y += 6;
      autoTable(doc, {
        startY: y,
        head: [["Mois", "Entrees", "Sorties", "Net", "Cumul"]],
        body: cfData.map(c => [
          new Date(c.month + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
          fmt(c.entrees), fmt(c.sorties), fmt(c.net), fmt(c.cumul)
        ]),
        theme: "grid",
        headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: "bold" },
        styles: { fontSize: 8 }, margin: { left: 14 },
      });
    }

    // Projection table
    if (projData?.projection?.length > 0) {
      doc.addPage("landscape");
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Projection 6 mois", 14, 18);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100);
      doc.text(`Moy. encaissements/mois: ${fmt(projData.avg_encaissements_mensuel)} | Moy. depenses/mois: ${fmt(projData.avg_depenses_mensuel)}`, 14, 25);
      doc.text(`Connu a encaisser: ${fmt(projData.connu_a_encaisser)} | Connu a payer: ${fmt(projData.connu_a_payer)}`, 14, 30);
      doc.setTextColor(0);

      autoTable(doc, {
        startY: 36,
        head: [["Mois", "Entrees prevues", "dont connu", "Sorties prevues", "dont connu", "Net", "Cumul"]],
        body: projData.projection.map(p => [
          new Date(p.month + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
          fmt(p.entrees_prevues), fmt(p.entrees_connues),
          fmt(p.sorties_prevues), fmt(p.sorties_connues),
          fmt(p.net_prevu), fmt(p.cumul_prevu)
        ]),
        theme: "grid",
        headStyles: { fillColor: [139, 92, 246], textColor: 255, fontStyle: "bold" },
        styles: { fontSize: 8 }, margin: { left: 14 },
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(150);
      doc.text(`Page ${i}/${pageCount} — Blitzz Platform`, 14, doc.internal.pageSize.height - 7);
    }

    doc.save(`tresorerie_${dashboard?.month || "rapport"}.pdf`);
  };

  // Merge cashflow + projection into one chart dataset
  const buildCombinedChart = () => {
    const real = (cashflow || []).map(c => ({
      month: c.month,
      entrees: c.entrees,
      sorties: c.sorties,
      net: c.net,
      cumul: c.cumul,
    }));
    const proj = (projData?.projection || []).map(p => ({
      month: p.month,
      entrees_prev: p.entrees_prevues,
      sorties_prev: p.sorties_prevues,
      net_prev: p.net_prevu,
      cumul_prev: p.cumul_prevu,
    }));
    // Add bridging point: last real month with projection start
    if (real.length > 0 && proj.length > 0) {
      const lastReal = real[real.length - 1];
      proj[0] = { ...proj[0], cumul: lastReal.cumul, cumul_prev: (lastReal.cumul || 0) + (proj[0].cumul_prev || 0) };
    }
    return [...real, ...proj];
  };

  const combined = buildCombinedChart();

  if (loading) {
    return (
      <div className="p-6 lg:p-10 space-y-6" data-testid="tresorerie-loading">
        <KpiSkeleton count={4} />
        <TableSkeleton rows={6} cols={4} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-full" data-testid="tresorerie-page">
      <PageHeader
        icon={<Landmark />}
        title="Trésorerie"
        description={`Vue globale Dépenses, Encaissements & CRM — ${dashboard?.month ? new Date(dashboard.month + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : ""}`}
        testId="tresorerie-header"
        actions={(
          <>
            <Button onClick={exportPDF} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs h-9" data-testid="export-pdf-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF
            </Button>
            <Button onClick={loadData} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs h-9" data-testid="refresh-tresorerie-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Actualiser
            </Button>
          </>
        )}
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 rounded-lg border px-1 py-1 mb-5 w-fit" style={{ borderColor: "var(--border-primary)" }}>
        {[
          { k: "overview", l: "Vue d'ensemble" },
          { k: "cashflow", l: "Cash-Flow" },
          { k: "projection", l: "Projection" },
          { k: "comparatif", l: "Comparatif N/N-1" },
          { k: "banque", l: "Banque" },
        ].map(v => (
          <button key={v.k} onClick={() => setActiveTab(v.k)} data-testid={`tab-${v.k}`}
            className={`px-4 py-1.5 rounded-md text-[11px] font-bold transition-colors ${activeTab === v.k ? "text-[#0b0811]" : ""}`}
            style={activeTab === v.k ? { background: "#f7c935" } : { color: "var(--text-muted)" }}>
            {v.l}
          </button>
        ))}
      </div>

      {/* ─── KPI Cards ─── */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-5">
        {[
          { label: "Solde net", val: fmt(kpis.solde_net), icon: Landmark, color: kpis.solde_net >= 0 ? "#22c55e" : "#ef4444", arrow: kpis.solde_net >= 0 ? ArrowUpRight : ArrowDownRight },
          { label: "Encaissements", val: fmt(kpis.total_encaissements), icon: TrendingUp, color: "#f7c935" },
          { label: "Regle", val: fmt(kpis.total_regle), icon: DollarSign, color: "#22c55e" },
          { label: "Depenses", val: fmt(kpis.total_depenses), icon: TrendingDown, color: "#ef4444" },
          { label: "Reste a regler", val: fmt(kpis.total_reste_a_regler), icon: Wallet, color: kpis.total_reste_a_regler > 0 ? "#f59e0b" : "#22c55e" },
          { label: "Recouvrement", val: `${kpis.taux_recouvrement || 0}%`, icon: BarChart3, color: kpis.taux_recouvrement >= 70 ? "#22c55e" : kpis.taux_recouvrement >= 50 ? "#f59e0b" : "#ef4444" },
        ].map((c) => (
          <div key={c.label} className="rounded-xl border p-4 relative overflow-hidden group" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid={`kpi-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="absolute inset-0 opacity-[0.04] group-hover:opacity-[0.08] transition-opacity" style={{ background: `radial-gradient(circle at 80% 20%, ${c.color}, transparent 70%)` }} />
            <div className="flex items-center gap-2 mb-2 relative">
              <c.icon className="w-4 h-4" style={{ color: c.color }} />
              <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>{c.label}</span>
            </div>
            <div className="text-lg font-black relative" style={{ color: "var(--text-primary)" }}>{c.val}</div>
          </div>
        ))}
      </div>

      {/* ─── Alerts ─── */}
      {alerts.length > 0 && (
        <div className="space-y-2 mb-5" data-testid="tresorerie-alerts">
          {alerts.map((a, i) => {
            const st = ALERT_STYLES[a.type] || ALERT_STYLES.info;
            const Icon = st.icon;
            return (
              <div key={i} className="flex items-center gap-3 rounded-lg border px-4 py-2.5" style={{ background: st.bg, borderColor: st.border }}>
                <Icon className="w-4 h-4 flex-shrink-0" style={{ color: st.text }} />
                <span className="text-xs font-medium" style={{ color: st.text }}>{a.message}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── TAB: Vue d'ensemble ─── */}
      {/* ─── Banque (imports + GoCardless + rapprochement) ─── */}
      {activeTab === "banque" && <BankTab api={api} />}

      {activeTab === "overview" && (
        <div className="space-y-5" data-testid="overview-section">
          {/* ─── Soldes bancaires réels (importés) ─── */}
          {bankSummary && bankSummary.transactions_count > 0 && (
            <div className="rounded-xl border p-4" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="bank-balance-card">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-[#3b82f6]" />
                  <span className="text-xs uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>Soldes bancaires (réel importé)</span>
                </div>
                <button onClick={() => setActiveTab("banque")} className="text-[10px] font-bold text-[#3b82f6] hover:underline" data-testid="bank-balance-goto-banque">
                  Gérer les imports →
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg border p-3" style={{ borderColor: "var(--border-primary)" }} data-testid="bank-balance-total">
                  <div className="text-[10px] uppercase font-bold mb-1" style={{ color: "var(--text-muted)" }}>Solde bancaire total</div>
                  <div className="text-xl font-black" style={{ color: bankSummary.solde_net_banque >= 0 ? "#22c55e" : "#ef4444" }}>{fmt(bankSummary.solde_net_banque)}</div>
                </div>
                <div className="rounded-lg border p-3" style={{ borderColor: "var(--border-primary)" }} data-testid="bank-balance-ecart">
                  <div className="text-[10px] uppercase font-bold mb-1" style={{ color: "var(--text-muted)" }}>Écart théorique vs réel</div>
                  {(() => {
                    const ecart = (bankSummary.solde_net_banque || 0) - (kpis.solde_net || 0);
                    const seuil = bankSummary.alert_config?.seuil ?? 500;
                    const color = Math.abs(ecart) < 1 ? "#22c55e" : Math.abs(ecart) > seuil ? "#ef4444" : "#f59e0b";
                    return (
                      <>
                        <div className="text-xl font-black" style={{ color }}>{ecart >= 0 ? "+" : ""}{fmt(ecart)}</div>
                        {Math.abs(ecart) > seuil && bankSummary.alert_config?.enabled !== false && (
                          <div className="text-[9px] font-bold text-red-400 mt-0.5" data-testid="ecart-over-seuil">⚠ Seuil {fmt(seuil)} dépassé</div>
                        )}
                      </>
                    );
                  })()}
                </div>
                {(bankSummary.accounts || []).slice(0, 6).map(a => (
                  <div key={a.label} className="rounded-lg border p-3" style={{ borderColor: "var(--border-primary)" }} data-testid={`bank-balance-account-${a.label.replace(/[^a-z0-9]/gi, "-")}`}>
                    <div className="text-[10px] uppercase font-bold mb-1 truncate" title={a.label} style={{ color: "var(--text-muted)" }}>{a.label}</div>
                    <div className="text-lg font-black" style={{ color: a.solde >= 0 ? "#22c55e" : "#ef4444" }}>{fmt(a.solde)}</div>
                    <div className="text-[9px]" style={{ color: "var(--text-muted)" }}>{a.count} opérations{a.last_date ? ` · ${a.last_date}` : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Depenses ce mois", val: kpis.nb_depenses, icon: Receipt, color: "#ef4444" },
              { label: "Encaissements ce mois", val: kpis.nb_encaissements, icon: PiggyBank, color: "#f7c935" },
              { label: "Factures totales", val: kpis.nb_factures, icon: FileText, color: "#3b82f6" },
              { label: "Factures impayees", val: kpis.nb_factures_impayees, icon: AlertTriangle, color: kpis.nb_factures_impayees > 0 ? "#ef4444" : "#22c55e" },
            ].map((c, i) => (
              <div key={i} className="rounded-xl border p-4 flex items-center gap-3" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: c.color + "15" }}>
                  <c.icon className="w-5 h-5" style={{ color: c.color }} />
                </div>
                <div>
                  <div className="text-xl font-black" style={{ color: "var(--text-primary)" }}>{c.val}</div>
                  <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{c.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ─── Objectifs du mois ─── */}
          {objectifs && (
            <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="objectifs-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4" style={{ color: "#f7c935" }} />
                  <h3 className="text-xs font-bold" style={{ color: "var(--text-primary)" }}>Objectifs du mois</h3>
                </div>
                <button onClick={openObjConfig} className="text-[10px] flex items-center gap-1 hover:underline transition-colors" style={{ color: "#f7c935" }} data-testid="config-objectifs-btn">
                  <Settings2 className="w-3 h-3" /> Configurer
                </button>
              </div>
              {objectifs.objectifs.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                  {objectifs.objectifs.map((obj, i) => {
                    const statusColors = { success: "#22c55e", warning: "#f59e0b", danger: "#ef4444" };
                    const col = statusColors[obj.status] || "#8b5cf6";
                    const pct = Math.min(obj.progress, 100);
                    const fmtVal = obj.unit === "EUR" ? fmt(obj.actual) : obj.unit === "%" ? `${obj.actual}%` : obj.actual;
                    const fmtTarget = obj.unit === "EUR" ? fmt(obj.target) : obj.unit === "%" ? `${obj.target}%` : obj.target;
                    return (
                      <div key={obj.type} className="rounded-lg border p-4 relative overflow-hidden" style={{ borderColor: "var(--border-primary)" }} data-testid={`objectif-${obj.type}`}>
                        <div className="absolute inset-0 opacity-[0.03]" style={{ background: `radial-gradient(circle at 50% 50%, ${col}, transparent 70%)` }} />
                        <div className="relative">
                          <span className="text-[10px] block mb-2 font-bold" style={{ color: "var(--text-muted)" }}>{obj.label}</span>
                          {/* Circular gauge */}
                          <div className="flex items-center gap-4">
                            <div className="relative w-16 h-16 flex-shrink-0">
                              <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                                <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
                                <circle cx="18" cy="18" r="15.5" fill="none" stroke={col} strokeWidth="3"
                                  strokeDasharray={`${pct * 0.974} 100`}
                                  strokeLinecap="round"
                                  className="transition-all duration-700"
                                />
                              </svg>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-[11px] font-black" style={{ color: col }}>{Math.round(obj.progress)}%</span>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-black" style={{ color: "var(--text-primary)" }}>{fmtVal}</div>
                              <div className="text-[10px]" style={{ color: "var(--text-dimmed)" }}>sur {fmtTarget}</div>
                              {obj.direction === "down" && (
                                <div className="text-[9px] mt-0.5" style={{ color: col }}>
                                  {obj.status === "success" ? "Dans le budget" : obj.status === "warning" ? "Limite atteinte" : "Budget depasse"}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>Aucun objectif defini pour ce mois</p>
                  <Button onClick={openObjConfig} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs h-8" data-testid="add-objectifs-btn">
                    <Target className="w-3 h-3 mr-1" /> Definir des objectifs
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top depenses categories */}
            <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="top-depenses-chart">
              <h3 className="text-xs font-bold mb-4" style={{ color: "var(--text-primary)" }}>Top categories depenses</h3>
              {(dashboard?.top_depenses || []).length > 0 ? (
                <div className="space-y-3">
                  {dashboard.top_depenses.map((d, i) => {
                    const maxVal = dashboard.top_depenses[0]?.total || 1;
                    const pct = Math.round((d.total / maxVal) * 100);
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs capitalize" style={{ color: "var(--text-primary)" }}>{d.category}</span>
                          <span className="text-xs font-bold" style={{ color: "#ef4444" }}>{fmt(d.total)}</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #ef4444, #f97316)" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-center py-6" style={{ color: "var(--text-muted)" }}>Aucune depense ce mois</p>
              )}
            </div>

            {/* Statuts encaissements */}
            <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="statuts-encaissements-chart">
              <h3 className="text-xs font-bold mb-4" style={{ color: "var(--text-primary)" }}>Statuts encaissements</h3>
              {(dashboard?.statuts_encaissements || []).length > 0 ? (
                <div className="space-y-3">
                  {dashboard.statuts_encaissements.map((s, i) => {
                    const colors = { regle: "#22c55e", en_cours: "#f7c935", en_attente: "#f59e0b", impaye: "#ef4444" };
                    const col = colors[s.statut] || "#8b5cf6";
                    const maxVal = dashboard.statuts_encaissements[0]?.total || 1;
                    const pct = Math.round((s.total / maxVal) * 100);
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs capitalize" style={{ color: "var(--text-primary)" }}>{s.statut.replace(/_/g, " ")}</span>
                          <span className="text-xs font-bold" style={{ color: col }}>{fmt(s.total)}</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: col }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-center py-6" style={{ color: "var(--text-muted)" }}>Aucun encaissement ce mois</p>
              )}
            </div>
          </div>

          {/* Mini cashflow preview */}
          <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="mini-cashflow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold" style={{ color: "var(--text-primary)" }}>Cash-Flow 12 mois</h3>
              <button onClick={() => setActiveTab("cashflow")} className="text-[10px] flex items-center gap-1 hover:underline" style={{ color: "#f7c935" }}>
                Voir detail <ChevronRight className="w-3 h-3" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={cashflow} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: "#888", fontSize: 10 }} tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{ fill: "#888", fontSize: 10 }} tickFormatter={fmtK} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="entrees" name="Entrees" fill="#22c55e" radius={[3, 3, 0, 0]} />
                <Bar dataKey="sorties" name="Sorties" fill="#ef4444" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ─── TAB: Cash-Flow ─── */}
      {activeTab === "cashflow" && (
        <div className="space-y-5" data-testid="cashflow-section">
          {/* Stacked bar chart — Entrees vs Sorties */}
          <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
            <h3 className="text-sm font-bold mb-1" style={{ color: "var(--text-primary)" }}>Entrees vs Sorties mensuelles</h3>
            <p className="text-[10px] mb-4" style={{ color: "var(--text-muted)" }}>Historique sur 12 mois glissants</p>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={cashflow} barGap={4}>
                <defs>
                  <linearGradient id="greenGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0.3} />
                  </linearGradient>
                  <linearGradient id="redGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.3} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: "#888", fontSize: 10 }} tickFormatter={v => {
                  try { return new Date(v + "-01").toLocaleDateString("fr-FR", { month: "short" }); } catch { return v?.slice(5); }
                }} />
                <YAxis tick={{ fill: "#888", fontSize: 10 }} tickFormatter={fmtK} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="entrees" name="Entrees" fill="url(#greenGrad)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="sorties" name="Sorties" fill="url(#redGrad)" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="cumul" name="Cumul" stroke="#f7c935" strokeWidth={2.5} dot={{ fill: "#f7c935", r: 3 }} />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" strokeDasharray="3 3" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Net mensuel area chart */}
          <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
            <h3 className="text-sm font-bold mb-1" style={{ color: "var(--text-primary)" }}>Solde net mensuel</h3>
            <p className="text-[10px] mb-4" style={{ color: "var(--text-muted)" }}>Entrees - Sorties par mois</p>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={cashflow}>
                <defs>
                  <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f7c935" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f7c935" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: "#888", fontSize: 10 }} tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{ fill: "#888", fontSize: 10 }} tickFormatter={fmtK} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
                <Area type="monotone" dataKey="net" name="Solde net" stroke="#f7c935" fill="url(#netGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly detail table */}
          <div className="rounded-xl border overflow-hidden" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="cashflow-table">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--border-primary)" }}>
                  {["Mois", "Entrees", "Sorties", "Net", "Cumul", "Nb Dep.", "Nb Enc."].map(h => (
                    <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cashflow.filter(c => c.entrees > 0 || c.sorties > 0).map((c, i) => (
                  <tr key={i} className="border-b hover:bg-white/[0.02]" style={{ borderColor: "rgba(255,255,255,0.03)" }}>
                    <td className="px-4 py-2.5 text-xs font-medium capitalize" style={{ color: "var(--text-primary)" }}>
                      {new Date(c.month + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-bold" style={{ color: "#22c55e" }}>{fmt(c.entrees)}</td>
                    <td className="px-4 py-2.5 text-xs font-bold" style={{ color: "#ef4444" }}>{fmt(c.sorties)}</td>
                    <td className="px-4 py-2.5 text-xs font-bold" style={{ color: c.net >= 0 ? "#22c55e" : "#ef4444" }}>{fmt(c.net)}</td>
                    <td className="px-4 py-2.5 text-xs font-bold" style={{ color: "#f7c935" }}>{fmt(c.cumul)}</td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: "var(--text-muted)" }}>{c.nb_dep}</td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: "var(--text-muted)" }}>{c.nb_enc}</td>
                  </tr>
                ))}
                {cashflow.filter(c => c.entrees > 0 || c.sorties > 0).length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-xs" style={{ color: "var(--text-muted)" }}>Aucune donnee de cash-flow disponible</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── TAB: Projection ─── */}
      {activeTab === "projection" && projData && (
        <div className="space-y-5" data-testid="projection-section">
          {/* Projection summary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Moy. encaissements/mois", val: fmt(projData.avg_encaissements_mensuel), color: "#22c55e" },
              { label: "Moy. depenses/mois", val: fmt(projData.avg_depenses_mensuel), color: "#ef4444" },
              { label: "Connu a encaisser", val: fmt(projData.connu_a_encaisser), color: "#f7c935", sub: "Ventile par mois" },
              { label: "Connu a payer", val: fmt(projData.connu_a_payer), color: "#f59e0b", sub: "Ventile par echeance" },
            ].map((c, i) => (
              <div key={i} className="rounded-xl border p-4" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
                <span className="text-[10px] uppercase tracking-wider font-bold block mb-1" style={{ color: "var(--text-muted)" }}>{c.label}</span>
                <span className="text-lg font-black" style={{ color: c.color }}>{c.val}</span>
                {c.sub && <span className="text-[9px] block mt-0.5" style={{ color: "var(--text-dimmed)" }}>{c.sub}</span>}
              </div>
            ))}
          </div>

          {/* Combined real + projection chart */}
          <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
            <h3 className="text-sm font-bold mb-1" style={{ color: "var(--text-primary)" }}>Cash-Flow reel + Projection 6 mois</h3>
            <p className="text-[10px] mb-4" style={{ color: "var(--text-muted)" }}>Les projections combinent les montants connus (echeances, restes a regler) + les tendances moyennes des 3 derniers mois</p>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={combined}>
                <defs>
                  <linearGradient id="cumulGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f7c935" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#f7c935" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="projGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: "#888", fontSize: 10 }} tickFormatter={v => {
                  try { return new Date(v + "-01").toLocaleDateString("fr-FR", { month: "short" }); } catch { return v?.slice(5); }
                }} />
                <YAxis tick={{ fill: "#888", fontSize: 10 }} tickFormatter={fmtK} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
                {/* Real data bars */}
                <Bar dataKey="entrees" name="Entrees reelles" fill="#22c55e" radius={[3, 3, 0, 0]} />
                <Bar dataKey="sorties" name="Sorties reelles" fill="#ef4444" radius={[3, 3, 0, 0]} />
                {/* Projection bars */}
                <Bar dataKey="entrees_prev" name="Entrees prevues" fill="#22c55e" fillOpacity={0.3} radius={[3, 3, 0, 0]} />
                <Bar dataKey="sorties_prev" name="Sorties prevues" fill="#ef4444" fillOpacity={0.3} radius={[3, 3, 0, 0]} />
                {/* Cumul lines */}
                <Line type="monotone" dataKey="cumul" name="Cumul reel" stroke="#f7c935" strokeWidth={2.5} dot={{ r: 3, fill: "#f7c935" }} connectNulls={false} />
                <Line type="monotone" dataKey="cumul_prev" name="Cumul projete" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3, fill: "#8b5cf6" }} connectNulls={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Projection table — enriched with known vs estimated */}
          <div className="rounded-xl border overflow-hidden" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="projection-table">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--border-primary)" }}>
                  {["Mois", "Entrees", "dont connu", "Sorties", "dont connu", "Net", "Cumul"].map(h => (
                    <th key={h} className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(projData.projection || []).map((p, i) => (
                  <tr key={i} className="border-b hover:bg-white/[0.02]" style={{ borderColor: "rgba(255,255,255,0.03)" }}>
                    <td className="px-3 py-2.5 text-xs font-medium capitalize" style={{ color: "var(--text-primary)" }}>
                      {new Date(p.month + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-bold" style={{ color: "#22c55e" }}>{fmt(p.entrees_prevues)}</td>
                    <td className="px-3 py-2.5">
                      {p.entrees_connues > 0 ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: "#22c55e15", color: "#22c55e" }}>{fmt(p.entrees_connues)}</span>
                      ) : <span className="text-[10px]" style={{ color: "var(--text-dimmed)" }}>-</span>}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-bold" style={{ color: "#ef4444" }}>{fmt(p.sorties_prevues)}</td>
                    <td className="px-3 py-2.5">
                      {p.sorties_connues > 0 ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: "#ef444415", color: "#ef4444" }}>{fmt(p.sorties_connues)}</span>
                      ) : <span className="text-[10px]" style={{ color: "var(--text-dimmed)" }}>-</span>}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-bold" style={{ color: p.net_prevu >= 0 ? "#22c55e" : "#ef4444" }}>{fmt(p.net_prevu)}</td>
                    <td className="px-3 py-2.5 text-xs font-bold" style={{ color: "#8b5cf6" }}>{fmt(p.cumul_prevu)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── TAB: Comparatif N vs N-1 ─── */}
      {activeTab === "comparatif" && comparatif && (() => {
        const c = comparatif;
        const ck = c.kpis || {};
        const monthlyData = (c.monthly || []).filter(m => m.entrees_n !== null || m.entrees_n1 > 0 || m.sorties_n1 > 0);

        const VariationBadge = ({ val }) => {
          if (val === 0) return <span className="text-[10px] text-slate-500">-</span>;
          const up = val > 0;
          return (
            <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${up ? "text-green-400" : "text-red-400"}`}>
              {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {up ? "+" : ""}{val}%
            </span>
          );
        };

        return (
          <div className="space-y-5" data-testid="comparatif-section">
            {/* Year labels */}
            <div className="flex items-center gap-3">
              <span className="text-xs px-3 py-1 rounded-full font-bold" style={{ background: "#f7c935", color: "#0b0811" }}>{c.year_n}</span>
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>vs</span>
              <span className="text-xs px-3 py-1 rounded-full font-bold border" style={{ borderColor: "var(--border-primary)", color: "var(--text-muted)" }}>{c.year_n1}</span>
              <span className="text-[10px] ml-2" style={{ color: "var(--text-dimmed)" }}>Comparaison jan-{new Date(c.year_n, c.current_month - 1).toLocaleDateString("fr-FR", { month: "short" })}</span>
            </div>

            {/* Comparative KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { label: "Entrees", n: ck.entrees_n, n1: ck.entrees_n1, var: ck.entrees_variation, color: "#22c55e" },
                { label: "Sorties", n: ck.sorties_n, n1: ck.sorties_n1, var: ck.sorties_variation, color: "#ef4444", invertVar: true },
                { label: "Solde net", n: ck.solde_n, n1: ck.solde_n1, var: ck.solde_variation, color: "#f7c935" },
              ].map((k) => (
                <div key={k.label} className="rounded-xl border p-5 relative overflow-hidden" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid={`comp-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="absolute inset-0 opacity-[0.04]" style={{ background: `radial-gradient(circle at 80% 20%, ${k.color}, transparent 70%)` }} />
                  <div className="relative">
                    <span className="text-[10px] uppercase tracking-wider font-bold block mb-3" style={{ color: "var(--text-muted)" }}>{k.label}</span>
                    <div className="flex items-end gap-4">
                      <div>
                        <span className="text-[10px] block" style={{ color: "#f7c935" }}>{c.year_n}</span>
                        <span className="text-xl font-black" style={{ color: k.color }}>{fmt(k.n)}</span>
                      </div>
                      <div>
                        <span className="text-[10px] block" style={{ color: "var(--text-dimmed)" }}>{c.year_n1}</span>
                        <span className="text-base font-bold" style={{ color: "var(--text-muted)" }}>{fmt(k.n1)}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <VariationBadge val={k.invertVar ? -k.var : k.var} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Comparative chart — Entrees N vs N-1 */}
            <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="comp-chart-entrees">
              <h3 className="text-sm font-bold mb-1" style={{ color: "var(--text-primary)" }}>Entrees mensuelles — {c.year_n} vs {c.year_n1}</h3>
              <p className="text-[10px] mb-4" style={{ color: "var(--text-muted)" }}>Comparaison des encaissements mois par mois</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyData} barGap={2} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="label" tick={{ fill: "#888", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#888", fontSize: 10 }} tickFormatter={fmtK} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="entrees_n1" name={`Entrees ${c.year_n1}`} fill="#22c55e" fillOpacity={0.25} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="entrees_n" name={`Entrees ${c.year_n}`} fill="#22c55e" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Comparative chart — Sorties N vs N-1 */}
            <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="comp-chart-sorties">
              <h3 className="text-sm font-bold mb-1" style={{ color: "var(--text-primary)" }}>Sorties mensuelles — {c.year_n} vs {c.year_n1}</h3>
              <p className="text-[10px] mb-4" style={{ color: "var(--text-muted)" }}>Comparaison des depenses mois par mois</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyData} barGap={2} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="label" tick={{ fill: "#888", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#888", fontSize: 10 }} tickFormatter={fmtK} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sorties_n1" name={`Sorties ${c.year_n1}`} fill="#ef4444" fillOpacity={0.25} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="sorties_n" name={`Sorties ${c.year_n}`} fill="#ef4444" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Solde net comparison line chart */}
            <div className="rounded-xl border p-5" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="comp-chart-net">
              <h3 className="text-sm font-bold mb-1" style={{ color: "var(--text-primary)" }}>Solde net mensuel — {c.year_n} vs {c.year_n1}</h3>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="label" tick={{ fill: "#888", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#888", fontSize: 10 }} tickFormatter={fmtK} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
                  <Line type="monotone" dataKey="net_n" name={`Net ${c.year_n}`} stroke="#f7c935" strokeWidth={2.5} dot={{ r: 3, fill: "#f7c935" }} connectNulls={false} />
                  <Line type="monotone" dataKey="net_n1" name={`Net ${c.year_n1}`} stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3, fill: "#8b5cf6" }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Detailed comparison table */}
            <div className="rounded-xl border overflow-hidden" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="comp-table">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b" style={{ borderColor: "var(--border-primary)" }}>
                    <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Mois</th>
                    <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-center" colSpan={2} style={{ color: "#22c55e" }}>Entrees</th>
                    <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-center" colSpan={2} style={{ color: "#ef4444" }}>Sorties</th>
                    <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-center" colSpan={2} style={{ color: "#f7c935" }}>Solde net</th>
                  </tr>
                  <tr className="border-b" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
                    <th className="px-3 py-1"></th>
                    <th className="px-2 py-1 text-[9px] text-center" style={{ color: "#f7c935" }}>{c.year_n}</th>
                    <th className="px-2 py-1 text-[9px] text-center" style={{ color: "var(--text-dimmed)" }}>{c.year_n1}</th>
                    <th className="px-2 py-1 text-[9px] text-center" style={{ color: "#f7c935" }}>{c.year_n}</th>
                    <th className="px-2 py-1 text-[9px] text-center" style={{ color: "var(--text-dimmed)" }}>{c.year_n1}</th>
                    <th className="px-2 py-1 text-[9px] text-center" style={{ color: "#f7c935" }}>{c.year_n}</th>
                    <th className="px-2 py-1 text-[9px] text-center" style={{ color: "var(--text-dimmed)" }}>{c.year_n1}</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyData.map((m, i) => (
                    <tr key={i} className="border-b hover:bg-white/[0.02]" style={{ borderColor: "rgba(255,255,255,0.03)" }}>
                      <td className="px-3 py-2 text-xs font-medium capitalize" style={{ color: "var(--text-primary)" }}>{m.label}</td>
                      <td className="px-2 py-2 text-xs font-bold text-center" style={{ color: "#22c55e" }}>{m.entrees_n != null ? fmt(m.entrees_n) : <span className="text-slate-600">-</span>}</td>
                      <td className="px-2 py-2 text-[11px] text-center" style={{ color: "var(--text-muted)" }}>{fmt(m.entrees_n1)}</td>
                      <td className="px-2 py-2 text-xs font-bold text-center" style={{ color: "#ef4444" }}>{m.sorties_n != null ? fmt(m.sorties_n) : <span className="text-slate-600">-</span>}</td>
                      <td className="px-2 py-2 text-[11px] text-center" style={{ color: "var(--text-muted)" }}>{fmt(m.sorties_n1)}</td>
                      <td className="px-2 py-2 text-xs font-bold text-center" style={{ color: m.net_n >= 0 ? "#22c55e" : "#ef4444" }}>{m.net_n != null ? fmt(m.net_n) : <span className="text-slate-600">-</span>}</td>
                      <td className="px-2 py-2 text-[11px] text-center" style={{ color: "var(--text-muted)" }}>{fmt(m.net_n1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ─── Dialog: Configurer les objectifs ─── */}
      <Dialog open={showObjConfig} onOpenChange={setShowObjConfig}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-md" data-testid="objectifs-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Target className="w-5 h-5 text-[#f7c935]" /> Objectifs du mois
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            Definissez vos objectifs pour {dashboard?.month ? new Date(dashboard.month + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "ce mois"}. Laissez a 0 pour desactiver un objectif.
          </p>
          <div className="space-y-3 mt-3">
            {objForm.map((o, i) => (
              <div key={o.type} className="flex items-center gap-3">
                <label className="text-xs flex-1 min-w-0" style={{ color: "var(--text-primary)" }}>{o.label}</label>
                <div className="flex items-center gap-1.5">
                  <Input type="number" value={o.target} onChange={e => {
                    const updated = [...objForm];
                    updated[i] = { ...updated[i], target: e.target.value };
                    setObjForm(updated);
                  }} placeholder="0" className="bg-[#0b0811] border-white/10 text-white w-28 h-8 text-xs text-right" data-testid={`obj-input-${o.type}`} />
                  <span className="text-[10px] w-6" style={{ color: "var(--text-dimmed)" }}>{o.unit === "EUR" ? "EUR" : o.unit === "%" ? "%" : ""}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end pt-3 mt-2 border-t border-white/5">
            <Button onClick={() => setShowObjConfig(false)} variant="outline" className="border-white/10 text-slate-400 h-8 text-xs">Annuler</Button>
            <Button onClick={saveObjectifs} disabled={savingObj} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-8 text-xs" data-testid="save-objectifs-btn">
              {savingObj ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Target className="w-3.5 h-3.5 mr-1" />}
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
